// ============================================================================
// BP-13-02: Analytics Charts Component
// BrightPath Platform - Admin Frontend
// ============================================================================

import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '@/core/lib/utils';
import type { TimeSeriesDataPoint, CategoryDataPoint } from '@/types/admin';

// =============================================================================
// Color Palette
// =============================================================================

const COLORS = {
  sage: '#7A9E7E',
  sageDark: '#5C7D60',
  sageLight: '#9DB8A0',
  terracotta: '#C4785A',
  navy: '#2B3A4D',
  warmGray: '#8B8178',
  success: '#4CAF9E',
  warning: '#F59E0B',
  error: '#EF4444',
};

const PIE_COLORS = [
  COLORS.sage,
  COLORS.terracotta,
  COLORS.navy,
  COLORS.success,
  COLORS.warning,
  '#6B8DD6',
  '#9B59B6',
  '#E67E22',
];

// =============================================================================
// Line Chart
// =============================================================================

interface LineChartProps {
  data: TimeSeriesDataPoint[];
  dataKey?: string;
  title?: string;
  color?: string;
  showGrid?: boolean;
  height?: number;
}

export function AnalyticsLineChart({
  data,
  dataKey = 'value',
  title,
  color = COLORS.sage,
  showGrid = true,
  height = 300,
}: LineChartProps) {
  return (
    <div>
      {title && (
        <h4 className="mb-4 font-semibold text-navy">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />}
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: COLORS.warmGray }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: COLORS.warmGray }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, fill: color }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Area Chart
// =============================================================================

interface AreaChartProps {
  data: TimeSeriesDataPoint[];
  dataKey?: string;
  title?: string;
  color?: string;
  height?: number;
  gradient?: boolean;
}

export function AnalyticsAreaChart({
  data,
  dataKey = 'value',
  title,
  color = COLORS.sage,
  height = 300,
  gradient = true,
}: AreaChartProps) {
  const gradientId = `gradient-${dataKey}`;

  return (
    <div>
      {title && (
        <h4 className="mb-4 font-semibold text-navy">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: COLORS.warmGray }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: COLORS.warmGray }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={2}
            fill={gradient ? `url(#${gradientId})` : color}
            fillOpacity={gradient ? 1 : 0.2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Bar Chart
// =============================================================================

interface BarChartProps {
  data: CategoryDataPoint[];
  dataKey?: string;
  title?: string;
  color?: string;
  height?: number;
  horizontal?: boolean;
}

export function AnalyticsBarChart({
  data,
  dataKey = 'value',
  title,
  color = COLORS.sage,
  height = 300,
  horizontal = false,
}: BarChartProps) {
  return (
    <div>
      {title && (
        <h4 className="mb-4 font-semibold text-navy">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 5, right: 20, left: horizontal ? 80 : 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          {horizontal ? (
            <>
              <XAxis
                type="number"
                tick={{ fontSize: 12, fill: COLORS.warmGray }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                type="category"
                dataKey="category"
                tick={{ fontSize: 12, fill: COLORS.warmGray }}
                tickLine={false}
                axisLine={false}
                width={70}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="category"
                tick={{ fontSize: 12, fill: COLORS.warmGray }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: COLORS.warmGray }}
                tickLine={false}
                axisLine={false}
              />
            </>
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Pie Chart
// =============================================================================

interface PieChartProps {
  data: CategoryDataPoint[];
  title?: string;
  height?: number;
  showLegend?: boolean;
  innerRadius?: number;
}

export function AnalyticsPieChart({
  data,
  title,
  height = 300,
  showLegend = true,
  innerRadius = 0,
}: PieChartProps) {
  return (
    <div>
      {title && (
        <h4 className="mb-4 font-semibold text-navy">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="category"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={80}
            paddingAngle={2}
            label={({ category, percent }) =>
              `${category}: ${(percent * 100).toFixed(0)}%`
            }
            labelLine={false}
          >
            {data.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={PIE_COLORS[index % PIE_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          {showLegend && (
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value) => (
                <span className="text-sm text-navy">{value}</span>
              )}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Multi-Line Chart
// =============================================================================

interface MultiLineChartProps {
  data: Record<string, unknown>[];
  lines: Array<{ dataKey: string; color: string; name: string }>;
  title?: string;
  height?: number;
}

export function AnalyticsMultiLineChart({
  data,
  lines,
  title,
  height = 300,
}: MultiLineChartProps) {
  return (
    <div>
      {title && (
        <h4 className="mb-4 font-semibold text-navy">{title}</h4>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: COLORS.warmGray }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: COLORS.warmGray }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          />
          <Legend
            verticalAlign="top"
            height={36}
            formatter={(value) => (
              <span className="text-sm text-navy">{value}</span>
            )}
          />
          {lines.map((line) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Stat Card
// =============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: React.ReactNode;
  className?: string;
}

export function StatCard({
  title,
  value,
  change,
  changeLabel,
  icon,
  className,
}: StatCardProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-gray-200 bg-white p-6',
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-warm-gray">{title}</p>
          <p className="mt-2 text-3xl font-semibold text-navy">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {change !== undefined && (
            <div className="mt-2 flex items-center gap-1">
              <span
                className={cn(
                  'inline-flex items-center text-sm font-medium',
                  change >= 0 ? 'text-emerald-600' : 'text-red-600'
                )}
              >
                {change >= 0 ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                )}
                {Math.abs(change)}%
              </span>
              {changeLabel && (
                <span className="text-sm text-warm-gray">{changeLabel}</span>
              )}
            </div>
          )}
        </div>
        {icon && (
          <div className="rounded-lg bg-sage/10 p-3 text-sage">
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Mini Spark Line
// =============================================================================

interface SparkLineProps {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
}

export function SparkLine({
  data,
  color = COLORS.sage,
  width = 80,
  height = 30,
}: SparkLineProps) {
  const chartData = data.map((value, index) => ({ value, index }));

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer>
        <LineChart data={chartData}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={1.5}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Main Analytics Charts Component
// =============================================================================

interface AnalyticsChartsProps {
  type: 'users' | 'engagement' | 'content' | 'marketplace';
  data: unknown;
  period: string;
}

export function AnalyticsCharts({ type, data, period }: AnalyticsChartsProps) {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-96 text-warm-gray">
        No data available
      </div>
    );
  }

  const renderUsersCharts = () => {
    const usersData = data as {
      new_users_trend?: Array<{ date: string; value: number }>;
      active_users_trend?: Array<{ date: string; value: number }>;
      users_by_role?: Array<{ category: string; value: number }>;
      total_users?: number;
      new_users?: number;
      active_users?: number;
      user_growth_rate?: number;
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard title="Total Users" value={usersData.total_users ?? 0} />
          <StatCard title="New Users" value={usersData.new_users ?? 0} change={usersData.user_growth_rate} />
          <StatCard title="Active Users" value={usersData.active_users ?? 0} />
          <StatCard title="Growth Rate" value={`${usersData.user_growth_rate ?? 0}%`} />
        </div>
        {usersData.new_users_trend && usersData.new_users_trend.length > 0 && (
          <AnalyticsAreaChart
            data={usersData.new_users_trend}
            title="New Users Trend"
            dataKey="value"
          />
        )}
        {usersData.users_by_role && usersData.users_by_role.length > 0 && (
          <AnalyticsPieChart
            data={usersData.users_by_role}
            title="Users by Role"
          />
        )}
      </div>
    );
  };

  const renderEngagementCharts = () => {
    const engagementData = data as {
      engagement_trend?: Array<{ date: string; value: number }>;
      feature_usage?: Array<{ category: string; value: number }>;
      total_page_views?: number;
      unique_page_views?: number;
      average_session_duration?: number;
      forum_posts_created?: number;
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard title="Page Views" value={engagementData.total_page_views ?? 0} />
          <StatCard title="Unique Views" value={engagementData.unique_page_views ?? 0} />
          <StatCard title="Avg Session" value={`${Math.round((engagementData.average_session_duration ?? 0) / 60)}m`} />
          <StatCard title="Forum Posts" value={engagementData.forum_posts_created ?? 0} />
        </div>
        {engagementData.engagement_trend && engagementData.engagement_trend.length > 0 && (
          <AnalyticsLineChart
            data={engagementData.engagement_trend}
            title="Engagement Trend"
            dataKey="value"
          />
        )}
        {engagementData.feature_usage && engagementData.feature_usage.length > 0 && (
          <AnalyticsBarChart
            data={engagementData.feature_usage}
            title="Feature Usage"
            horizontal
          />
        )}
      </div>
    );
  };

  const renderContentCharts = () => {
    const contentData = data as {
      resources_by_category?: Array<{ category: string; value: number }>;
      total_resources?: number;
      published_resources?: number;
      resource_views?: number;
      average_resource_rating?: number;
      total_forum_posts?: number;
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard title="Total Resources" value={contentData.total_resources ?? 0} />
          <StatCard title="Published" value={contentData.published_resources ?? 0} />
          <StatCard title="Resource Views" value={contentData.resource_views ?? 0} />
          <StatCard title="Avg Rating" value={(contentData.average_resource_rating ?? 0).toFixed(1)} />
        </div>
        {contentData.resources_by_category && contentData.resources_by_category.length > 0 && (
          <AnalyticsPieChart
            data={contentData.resources_by_category}
            title="Resources by Category"
          />
        )}
      </div>
    );
  };

  const renderMarketplaceCharts = () => {
    const marketplaceData = data as {
      vendor_growth_trend?: Array<{ date: string; value: number }>;
      vendors_by_category?: Array<{ category: string; value: number }>;
      total_vendors?: number;
      active_vendors?: number;
      total_products?: number;
      pending_verifications?: number;
    };

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard title="Total Vendors" value={marketplaceData.total_vendors ?? 0} />
          <StatCard title="Active Vendors" value={marketplaceData.active_vendors ?? 0} />
          <StatCard title="Total Products" value={marketplaceData.total_products ?? 0} />
          <StatCard title="Pending" value={marketplaceData.pending_verifications ?? 0} />
        </div>
        {marketplaceData.vendor_growth_trend && marketplaceData.vendor_growth_trend.length > 0 && (
          <AnalyticsAreaChart
            data={marketplaceData.vendor_growth_trend}
            title="Vendor Growth"
            dataKey="value"
          />
        )}
        {marketplaceData.vendors_by_category && marketplaceData.vendors_by_category.length > 0 && (
          <AnalyticsBarChart
            data={marketplaceData.vendors_by_category}
            title="Vendors by Category"
          />
        )}
      </div>
    );
  };

  switch (type) {
    case 'users':
      return renderUsersCharts();
    case 'engagement':
      return renderEngagementCharts();
    case 'content':
      return renderContentCharts();
    case 'marketplace':
      return renderMarketplaceCharts();
    default:
      return <div>Unknown analytics type</div>;
  }
}

// =============================================================================
// Exports
// =============================================================================

export default AnalyticsCharts;
